import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "./ConfirmDialog";
import { ActionList, type ActionListItem } from "./Cards";
import { useAuth } from "../utils/useAuth";
import { useSignOut } from "../utils/useSignOut";
import "./ui.css";

/**
 * AccountMenu – účet v hlavičce AppShellu: kulatý avatar s iniciálami
 * v pravém slotu (AppShell ho ukazuje jen přihlášenému a jen na obrazovce
 * bez tlačítka Zpět). Klepnutí otevře dialog „Přihlášený účet": jméno,
 * e-mail účtu, členské číslo, u správců role a akce Můj profil / Odhlásit
 * se – odhlášení je totéž jako v Profilu (useSignOut).
 *
 * Jméno a číslo dodává AuthProvider ze čtení users/{uid}, které už dělá
 * kvůli roli; obnovují ho obrazovky s useCurrentUser (utils/authProfile).
 * Komponenta sama nic nečte.
 */

/** Celé jméno ("Jan Novák"); bez jména i příjmení "". */
export function fullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
}

/** První znak textu velkým písmenem (celý znak i mimo základní rovinu Unicode). */
const firstLetter = (value: string): string => {
  const chars = Array.from(value.trim().normalize("NFC"));
  return chars.length > 0 ? chars[0].toLocaleUpperCase("cs-CZ") : "";
};

/** Iniciály ("Jan", "Novák" → "JN"); bez jména i příjmení "". */
export function initialsOf(firstName: string, lastName: string): string {
  return firstLetter(firstName) + firstLetter(lastName);
}

/** Kruhový avatar: iniciály, bez jména ikona bi-person-circle, při odhlašování kolečko. */
const Avatar: React.FC<{ initials: string; large?: boolean; busy?: boolean }> = ({
  initials,
  large = false,
  busy = false,
}) => (
  <span
    className={large ? "lks-avatar lks-avatar--lg" : "lks-avatar"}
    aria-hidden="true"
  >
    {busy ? (
      <span className="spinner-border spinner-border-sm" />
    ) : initials ? (
      initials
    ) : (
      <i className="bi bi-person-circle" />
    )}
  </span>
);

export const AccountMenu: React.FC = () => {
  const { user, ready, role, isSuperAdmin, profile, profileStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, signingOut } = useSignOut("AccountMenu");
  const [open, setOpen] = useState(false);
  const identityRef = useRef<HTMLDivElement>(null);

  // Navigace (i hardwarové Zpět) dialog zavírá – jinak by zůstal viset
  // nad další obrazovkou.
  useEffect(() => {
    setOpen(false);
  }, [location.key]);

  if (!ready || !user) return null;

  const name = profile ? fullName(profile.firstName, profile.lastName) : "";
  const initials = profile ? initialsOf(profile.firstName, profile.lastName) : "";
  const email = user.email || "";
  const label = name || email;
  const memberNumber = profile ? profile.memberNumber : null;
  // Stejné pravidlo jako isSuperAdmin v useAuth (role i permanentní e-mail).
  const roleLabel = isSuperAdmin
    ? "Super admin"
    : role === "admin"
    ? "Správce"
    : null;

  const close = () => setOpen(false);

  const openProfile = () => {
    setOpen(false);
    if (location.pathname !== "/profile") navigate("/profile");
  };

  // Účet bez profilu dokončí registraci na přihlašovací stránce (Jste to
  // vy? / Doplňte údaje / Dokončit smazání) – stejně jako „Profil nenalezen“.
  const finishRegistration = () => {
    setOpen(false);
    navigate("/login");
  };

  // Dialog účtu se zavře dřív, než se otevře potvrzení odhlášení – dva
  // modaly přes sebe ne (fokus se po potvrzení vrátí na avatar).
  const handleSignOut = () => {
    setOpen(false);
    signOut();
  };

  // Běžící odhlášení čeká na neodeslané zápisy (offline až 3 s) a pak se může
  // ptát znovu („Neodeslané změny"): avatar mezitím ukazuje kolečko a dialog
  // účtu se neotevře ani nezůstane otevřený – potvrzení by vyskočilo nad ním.
  const handleOpen = () => {
    if (!signingOut) setOpen(true);
  };
  const buttonLabel = signingOut
    ? "Odhlašuji…"
    : label
    ? `Účet – ${label}`
    : "Účet";

  const items: ActionListItem[] = [
    profileStatus === "missing"
      ? { icon: "bi-person-check", label: "Dokončit registraci", onClick: finishRegistration }
      : { icon: "bi-person", label: "Můj profil", onClick: openProfile },
    {
      icon: "bi-box-arrow-right",
      label: "Odhlásit se",
      onClick: handleSignOut,
      tone: "danger",
      chevron: false,
    },
  ];

  return (
    <>
      <button
        type="button"
        className="lks-header__account"
        aria-label={buttonLabel}
        aria-haspopup="dialog"
        aria-disabled={signingOut ? true : undefined}
        onClick={handleOpen}
      >
        <Avatar initials={initials} busy={signingOut} />
      </button>

      <Modal
        open={open && !signingOut}
        onClose={close}
        title="Přihlášený účet"
        size="sm"
        initialFocusRef={identityRef}
      >
        {/* Fokus po otevření sem (tabIndex -1): čtečka přečte, kdo je přihlášený. */}
        <div className="lks-account" ref={identityRef} tabIndex={-1}>
          <Avatar initials={initials} large />
          <div className="lks-account__body">
            {name ? <div className="lks-account__name">{name}</div> : null}
            {email ? <div className="lks-account__email">{email}</div> : null}
            {memberNumber ? (
              <div className="lks-account__meta">
                Členské číslo <strong>{memberNumber}</strong>
              </div>
            ) : null}
            {profileStatus === "missing" ? (
              <div className="lks-account__meta">Profil není dokončený</div>
            ) : null}
            {roleLabel ? (
              <span className="badge lks-account__role">{roleLabel}</span>
            ) : null}
          </div>
        </div>
        <ActionList items={items} className="lks-account__actions" />
      </Modal>
    </>
  );
};

export default AccountMenu;
